import { useEffect, useState } from "preact/hooks"
import useSWR from "swr"
import { api_fetch_throws } from "../../../api/client"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Input } from "../../../components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { fmt_money, fmt_qty, month_label } from "./format"
import type { StoreRow, StoresView } from "./types"

/** Wait for typing to pause before searching. */
function useDebounced(value: string, ms: number): string {
  const [debounced, set_debounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => set_debounced(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return debounced
}

export function StoresTab({ className }: { className?: string }) {
  const [search, set_search] = useState("")
  const query = useDebounced(search, 300)
  const { data, isLoading } = useSWR(["kehe-supply-stores", query], () =>
    api_fetch_throws<StoresView>(`/distributor-supply/stores?source=kehe&limit=300${query ? `&search=${encodeURIComponent(query)}` : ""}`)
  )
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between gap-4 pb-2">
        <CardTitle className="text-base">
          KeHE stores {data ? <span class="text-sm font-normal text-gray-500">({fmt_qty(data.total)})</span> : null}
        </CardTitle>
        <Input
          className="max-w-xs"
          placeholder="Search store, city, state or retailer"
          value={search}
          onInput={(e: Event) => set_search((e.target as HTMLInputElement).value)}
        />
      </CardHeader>
      <CardContent className="max-h-[640px] overflow-auto">
        {isLoading && !data ? <p class="text-sm text-gray-500">Loading…</p> : null}
        {data && !data.stores.length ? <p class="text-sm text-gray-600">No stores match.</p> : null}
        {data?.stores.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Store</TableHead>
                <TableHead>Retailer</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>DC</TableHead>
                <TableHead>Last received</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead className="text-right">Units (3 mo)</TableHead>
                <TableHead className="text-right">Cost (3 mo)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.stores.map((s: StoreRow) => (
                <TableRow key={s.store_number}>
                  <TableCell>
                    {s.name} <span class="text-xs text-gray-500">#{s.store_number}</span>
                  </TableCell>
                  <TableCell>{s.retailer_name ?? s.retailer_parent ?? "—"}</TableCell>
                  <TableCell className="text-xs">{[s.address_line_1, s.city, s.state, s.postal_code].filter(Boolean).join(", ")}</TableCell>
                  <TableCell className="text-xs">{s.channel ?? "—"}</TableCell>
                  <TableCell>{s.dc_code ?? "—"}</TableCell>
                  <TableCell>{s.last_month ? month_label(s.last_month) : "—"}</TableCell>
                  <TableCell className="text-right">{s.items_3m || "—"}</TableCell>
                  <TableCell className="text-right">{fmt_qty(s.qty_3m)}</TableCell>
                  <TableCell className="text-right">{fmt_money(s.cost_3m)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  )
}
